import axios from 'axios';
import toast from 'react-hot-toast';
import { AssessableFacilitiesResponse } from '../hooks/useAssessibleFacilities';
import type {
  BulkImportResultDto,
  BulkCentralLevelResultDto,
  BulkTransferLocationsResultDto,
  AssessmentDto,
  AssessmentFineDto,
  FinesResponseDto,
  AssessmentRecommendationDto,
  AssessmentResponseDto,
  AssessmentsResponseDto,
  AssessmentSummaryDto,
  FacilityQuestionnaireResponsesApiDto,
  FacilityQuestionnaireResponsesDto,
  AssignLocationDto,
  AuditLogsResponseDto,
  ComplianceChecklistStatusDto,
  ComplianceQuestionBreakdownDto,
  ComplianceQuestionDto,
  ComplianceQuestionManagementDto,
  ComplianceQuestionnaireDetailDto,
  ComplianceQuestionnaireListItemDto,
  ComplianceQuestionOptionDto,
  ComplianceQuestionTreeDto,
  CreateComplianceOptionDto,
  CreateComplianceQuestionDto,
  CreateComplianceQuestionnaireDto,
  CreateComplianceQuestionInQuestionnaireDto,
  CurrentComplianceQuestionnaireResponseDto,
  UpdateComplianceQuestionnaireDto,
  CopyQuestionnaireDto,
  CreateFacilitySubcategoryDto,
  UpdateFacilitySubcategoryDto,
  FacilitySubcategoryDto,
  UpdateFacilityTypeDto,
  CreateHygieneOptionDto,
  CreateHygieneQuestionDto,
  CreateQuestionnaireDto,
  CreateQuestionnaireSectionDto,
  UpdateQuestionnaireSectionDto,
  QuestionnaireSectionDto,
  HygieneQuestionnaireDetailDto,
  HygieneQuestionnaireListItemDto,
  HygieneQuestionDto,
  HygieneOptionDto,
  UpdateComplianceOptionDto,
  UpdateComplianceQuestionDto,
  UpdateHygieneOptionDto,
  UpdateHygieneQuestionDto,
  UpdateQuestionnaireDto,
  CreateAssessmentDto,
  CreateFacilityCountDto,
  CreateFacilityDto,
  DecisionsAnalyticsDto,
  DecisionsAnalyticsPageDto,
  RecommendationListItemDto,
  RecommendationsListResponseDto,
  FacilitiesRequirementStatusApiResponseDto,
  FacilitiesRequirementStatusDto,
  FacilitiesRankingResponseDto,
  FacilitiesResponseDto,
  FacilityRegistrationAnalyticsDto,
  FacilityCountDto,
  FacilityCountResponseDto,
  FacilityCountsResponseDto,
  FlaggedAssessmentsResponseDto,
  FacilityDto,
  FacilityResponseDto,
  FacilityTypeDto,
  CreateFacilityTypeDto,
  FollowupAnalyticsDto,
  GeoLocationDto,
  HygieneByFacilityTypeDto,
  HygieneTrendPointDto,
  ILoginResponseDto,
  LocationRankingApiResponseDto,
  LocationRankingDto,
  LoginDto,
  LoginResponseDto,
  ObjectiveAnalyticsApiResponseDto,
  ObjectiveAnalyticsResponseDto,
  PendingRecommendationsDto,
  PaginationDto,
  QuestionDto,
  QuestionnaireAnalyticsDto,
  QuestionnaireAnalyticsResponseDto,
  QuestionnaireDocumentDto,
  QuestionnaireDto,
  RegisterDto,
  RegisterResponseDto,
  RoleDto,
  RolesResponseDto,
  CreateRoleDto,
  UpdateRoleDto,
  PermissionDto,
  PermissionsResponseDto,
  ScoreDistributionBucketDto,
  StandingsPerObjectiveApiResponseDto,
  StandingsPerObjectiveDto,
  UnassessedFacilitiesDto,
  UnassessedFacilitiesResponseDto,
  UpdateAssessmentSignatureDto,
  UpdateFacilityCountDto,
  UpdateFacilityDto,
  UpdateUserDto,
  CreateUserDto,
  UpdateUserStatusDto,
  UserDto,
  UserLocationHierarchyDto,
  UserResponseDto,
  UsersResponseDto,
  AssessmentAppeal,
  PublicAssessmentDetailsDto,
  SurveyDto,
  SurveyAnswers,
  SurveyLocationResponseDto,
  SurveyResponseDto,
  SurveyTrackingDto,
  BeverageAuthorizationListDto,
  BeverageAuthorizationDetailDto,
  BeverageAuthorizationStatus,
  BeverageEventPurpose,
  BeverageInspectionDto,
  BeverageDashboardDto,
  BeverageMonitoringDto,
  BeverageTypeDto,
  BeveragePaginationMeta,
  CreateBeverageInspectionDto,
  BeverageInspectionPhase,
  BeverageQuestionTreeDto,
  BeverageQuestionOptionDto,
  BeverageInspectionQuestionnaireListItemDto,
  BeverageInspectionQuestionnaireDetailDto,
  CurrentBeverageInspectionQuestionnaireResponseDto,
  CreateBeverageInspectionQuestionnaireDto,
  UpdateBeverageInspectionQuestionnaireDto,
  CopyBeverageInspectionQuestionnaireDto,
  CreateBeverageInspectionQuestionDto,
  CreateBeverageInspectionQuestionForQuestionnaireDto,
  UpdateBeverageInspectionQuestionDto,
  CreateBeverageInspectionOptionDto,
  UpdateBeverageInspectionOptionDto,
  CreateBeverageInspectionSubQuestionDto,
  SmsTemplateDto,
} from '../types/api';
import {
  normalizeComplianceQuestionTree,
  normalizeQuestionnaireSections,
  parseCurrentComplianceQuestionnaireResponse,
} from './complianceQuestionnaireNormalize';
import {
  normalizeBeverageQuestionTree,
  parseCurrentBeverageInspectionQuestionnaireResponse,
} from './beverageQuestionnaireNormalize';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(config => {
  const token = localStorage.getItem('access_token');
  const url = config.url ?? '';
  const isAuthLoginRequest = url.includes('/auth/login') || url.includes('/auth/register');
  // For login/register we should not attach an expired/invalid bearer token.
  // Some backends validate Authorization first and can return 401 "Invalid credentials"
  // even when the email/password are correct.
  if (token && !isAuthLoginRequest) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** Clears every trace of the session (tokens, stored user, cookies) and returns to login. */
const forceLogout = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('user');
  // Best-effort cookie clearing (the app is token-based, but clear any session cookies too)
  document.cookie.split(';').forEach(cookie => {
    const name = cookie.split('=')[0]?.trim();
    if (!name) return;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  });

  const isAuthPage = window.location.pathname.includes('/login') || window.location.pathname.includes('/register');
  if (!isAuthPage) {
    window.location.href = '/login';
  }
};

// Response interceptor for error handling.
// Only 401 (invalid/expired session) ends the session. 403 is a legitimate
// "no permission for this action" and must NOT log the user out — stale
// sessions are forced to re-login via the backend AUTH_TOKENS_VALID_FROM
// cutoff (which returns 401), not via 403.
apiClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      const isAuthRequest = error.config?.url?.includes('/auth/login') || error.config?.url?.includes('/auth/register');
      if (!isAuthRequest) {
        forceLogout();
      }
    }
    return Promise.reject(error);
  }
);

// Public API client — no auth token (for unauthenticated endpoints like appeals)
const publicApiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  },
});

// Error handling utility
export const handleApiError = (error: any): string => {
  console.error('API Error:', error);

  if (error.response?.data?.message) {
    toast.error(error.response.data.message);
    return error.response.data.message;
  }

  const message = error.message || 'An error occurred';
  toast.error(message);
  return message;
};

// Authentication API
export const authApi = {
  register: async (data: RegisterDto): Promise<RegisterResponseDto> => {
    const response = await apiClient.post('/api/v1/auth/register', data);
    return response.data;
  },

  login: async (data: LoginDto): Promise<ILoginResponseDto> => {
    const response = await apiClient.post<{ success: boolean; data: ILoginResponseDto }>('/api/v1/auth/login', data);
    return response.data.data;
  },

  requestPasswordReset: async (email: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/api/v1/auth/request-password-reset', { email });
    return response.data;
  },

  verifyOtp: async (email: string, otp: string): Promise<{ valid: boolean; message: string }> => {
    const response = await apiClient.post('/api/v1/auth/verify-otp', { email, otp });
    return response.data;
  },

  resetPasswordWithOtp: async (email: string, otp: string, newPassword: string): Promise<{ message: string }> => {
    const response = await apiClient.post('/api/v1/auth/reset-password', { email, otp, newPassword });
    return response.data;
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post('/api/v1/auth/change-password', { currentPassword, newPassword });
    return response.data;
  },
};

// Users API
export const usersApi = {
  getAll: async (params?: {
    status?: 'ACTIVE' | 'INACTIVE';
    search?: string;
    roleId?: string;
    geoLocationId?: string;
    page?: number;
    limit?: number;
  }): Promise<{ data: UserDto[]; total: number; page: number; limit: number }> => {
    const response = await apiClient.get<UsersResponseDto>('/api/v1/users', {
      params: params ? { ...params } : undefined,
    });
    return {
      data: response.data.data,
      total: response.data.pagination?.totalItems ?? response.data.total ?? response.data.data.length,
      page: response.data.pagination?.currentPage ?? response.data.page ?? 1,
      limit: response.data.pagination?.itemsPerPage ?? response.data.limit ?? response.data.data.length,
    };
  },

  getById: async (id: string): Promise<UserDto> => {
    const response = await apiClient.get<UserResponseDto>(`/api/v1/users/${id}`);
    return response.data.data;
  },

  create: async (data: CreateUserDto): Promise<UserDto> => {
    const response = await apiClient.post<UserResponseDto>('/api/v1/users', data);
    return response.data.data;
  },

  updateStatus: async (id: string, data: UpdateUserStatusDto): Promise<UserDto> => {
    const response = await apiClient.patch<UserResponseDto>(`/api/v1/users/${id}/status`, data);
    return response.data.data;
  },

  approve: async (id: string): Promise<UserDto> => {
    const response = await apiClient.post<UserResponseDto>(`/api/v1/users/${id}/approve`);
    return response.data.data;
  },

  reject: async (id: string, reason: string): Promise<UserDto> => {
    const response = await apiClient.put<UserResponseDto>('/api/v1/users/reject-user', { id, reason });
    return response.data.data;
  },

  assignLocations: async (id: string, data: AssignLocationDto): Promise<UserDto> => {
    const response = await apiClient.post<UserResponseDto>(`/api/v1/users/${id}/assign-locations`, data);
    return response.data.data;
  },

  getAssignedLocations: async (id: string) => {
    const response = await apiClient.get(`/api/v1/users/${id}/locations`);
    return response.data;
  },

  updateLocations: async (id: string, data: AssignLocationDto): Promise<UserDto> => {
    const response = await apiClient.patch<UserResponseDto>(`/api/v1/users/${id}/locations`, data);
    return response.data.data;
  },

  removeLocation: async (userId: string, geoLocationId: string) => {
    const response = await apiClient.delete<{ success: boolean; data: Array<{ id: string; geoLocation: { id: string; name: string; locationType: string } }> }>(
      `/api/v1/users/${userId}/locations/${geoLocationId}`
    );
    return response.data.data;
  },

  getMyLocationHierarchy: async (): Promise<UserLocationHierarchyDto> => {
    const response = await apiClient.get('/api/v1/users/me/location-hierarchy');
    return response.data.data;
  },

  update: async (id: string, data: UpdateUserDto): Promise<UserDto> => {
    const response = await apiClient.patch<UserResponseDto>(`/api/v1/users/${id}`, data);
    return response.data.data;
  },

  downloadOnboardingTemplate: async (): Promise<void> => {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_URL}/api/v1/users/onboard/template`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to download template');
    const blob = await response.blob();
    const filename =
      response.headers.get('Content-Disposition')?.match(/filename="(.+?)"/)?.[1] ?? 'onboarding_template.csv';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  bulkImport: async (file: File): Promise<BulkImportResultDto> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<{ success: boolean; data: BulkImportResultDto }>(
      '/api/v1/users/onboard/bulk',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  },

  bulkToggleCentralLevel: async (emails: string[], isCentralLevel: boolean): Promise<BulkCentralLevelResultDto> => {
    const response = await apiClient.patch<{ success: boolean; data: BulkCentralLevelResultDto }>(
      '/api/v1/users/bulk/central-level',
      { emails, isCentralLevel }
    );
    return response.data.data;
  },

  downloadBulkTransferLocationsTemplate: async (): Promise<void> => {
    const token = localStorage.getItem('access_token');
    const response = await fetch(`${API_URL}/api/v1/users/bulk-transfer-locations/template`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to download template');
    const blob = await response.blob();
    const filename =
      response.headers.get('Content-Disposition')?.match(/filename="(.+?)"/)?.[1] ?? 'bulk_transfer_locations_template.csv';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  bulkTransferLocations: async (file: File, dryRun: boolean): Promise<BulkTransferLocationsResultDto> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<{ success: boolean; data: BulkTransferLocationsResultDto }>(
      `/api/v1/users/bulk-transfer-locations?dryRun=${dryRun}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  },
};

// Roles API
export const rolesApi = {
  getAll: async (params?: { page?: number; limit?: number }) => {
    const response = await apiClient.get<RolesResponseDto>('/api/v1/roles', { params });
    return {
      data: response.data.data,
      pagination: response.data.pagination,
    };
  },

  getById: async (id: string): Promise<RoleDto> => {
    const response = await apiClient.get<RoleDto>(`/api/v1/roles/${id}`);
    return response.data;
  },

  getPermissions: async (): Promise<PermissionDto[]> => {
    const response = await apiClient.get<PermissionsResponseDto>('/api/v1/roles/permissions');
    return response.data.data;
  },

  create: async (data: CreateRoleDto): Promise<RoleDto> => {
    const response = await apiClient.post<RoleDto>('/api/v1/roles', data);
    return response.data;
  },

  update: async (id: string, data: UpdateRoleDto): Promise<RoleDto> => {
    const response = await apiClient.patch<RoleDto>(`/api/v1/roles/${id}`, data);
    return response.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/roles/${id}`);
  },
};

// Facilities API
export const facilitiesApi = {
  getAll: async (params?: {
    locationId?: string;
    facilityTypeId?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    data: FacilityDto[];
    pagination?: PaginationDto;
    total: number;
    page: number;
    limit: number;
  }> => {
    const response = await apiClient.get<FacilitiesResponseDto>('/api/v1/facilities', { params });
    return {
      data: response.data.data,
      pagination: response.data.pagination,
      // Keep legacy fields for backward compatibility
      total: response.data.pagination?.totalItems || response.data.total || response.data.data.length,
      page: response.data.pagination?.currentPage || response.data.page || 1,
      limit: response.data.pagination?.itemsPerPage || response.data.limit || response.data.data.length,
    };
  },

  getById: async (id: string): Promise<FacilityDto> => {
    const response = await apiClient.get<FacilityResponseDto>(`/api/v1/facilities/${id}`);
    return response.data.data;
  },

  create: async (data: CreateFacilityDto): Promise<FacilityDto> => {
    const response = await apiClient.post<FacilityResponseDto>('/api/v1/facilities', data);
    return response.data.data;
  },

  update: async (id: string, data: UpdateFacilityDto): Promise<FacilityDto> => {
    const response = await apiClient.patch<FacilityResponseDto>(`/api/v1/facilities/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/facilities/${id}`);
  },

  exportExcel: async (params?: {
    locationId?: string;
    facilityTypeId?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
  }): Promise<void> => {
    const token = localStorage.getItem('access_token');
    const query = new URLSearchParams(
      Object.fromEntries(Object.entries(params ?? {}).filter(([, v]) => v != null)) as Record<string, string>
    ).toString();
    const response = await fetch(`${API_URL}/api/v1/facilities/export${query ? `?${query}` : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    const filename =
      response.headers.get('Content-Disposition')?.match(/filename="(.+?)"/)?.[1] ?? 'facilities.xlsx';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  getAssessableFacilities: async (params?: {
    facilityTypeId?: string;
    subcategoryId?: string;
    weekNumber?: number;
    year?: number;
    locationId?: string;
  }): Promise<AssessableFacilitiesResponse> => {
    const response = await apiClient.get('/api/v1/assessments/assessable', { params });
    return response.data.data;
  },

  getDropdown: async (params?: { locationId?: string; facilityTypeId?: string }): Promise<FacilityDto[]> => {
    const response = await apiClient.get<FacilitiesResponseDto>('/api/v1/facilities/dropdown', { params });
    return response.data.data;
  },
};

// Assessments API
export const assessmentsApi = {
  getAll: async (params?: {
    userId?: string;
    userIds?: string[];
    facilityId?: string;
    locationId?: string;
    facilityTypeId?: string;
    questionnaireId?: string;
    assessmentType?: 'HYGIENE' | 'COMPLIANCE';
    weekNumber?: number;
    year?: number;
    startDate?: string;
    endDate?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    data: AssessmentDto[];
    pagination?: PaginationDto;
    total: number;
    page: number;
    limit: number;
  }> => {
    const { userIds, ...rest } = params ?? {};
    const serialized = userIds?.length ? { ...rest, userIds: userIds.join(',') } : rest;
    const response = await apiClient.get<AssessmentsResponseDto>('/api/v1/assessments', { params: serialized });
    return {
      data: response.data.data,
      pagination: response.data.pagination,
      // Keep legacy fields for backward compatibility
      total: response.data.pagination?.totalItems || response.data.total || response.data.data.length,
      page: response.data.pagination?.currentPage || response.data.page || 1,
      limit: response.data.pagination?.itemsPerPage || response.data.limit || response.data.data.length,
    };
  },

  getById: async (id: string): Promise<AssessmentDto> => {
    const response = await apiClient.get<AssessmentResponseDto>(`/api/v1/assessments/${id}`);
    return response.data.data;
  },

  create: async (data: CreateAssessmentDto): Promise<AssessmentDto> => {
    const response = await apiClient.post<AssessmentResponseDto>('/api/v1/assessments', data);
    return response.data.data;
  },

  updateSignature: async (id: string, data: UpdateAssessmentSignatureDto): Promise<AssessmentDto> => {
    const response = await apiClient.patch<AssessmentResponseDto>(`/api/v1/assessments/${id}/signature`, data);
    return response.data.data;
  },

  getUnassessed: async (params?: {
    weekNumber?: number;
    year?: number;
    startDate?: string;
    endDate?: string;
    locationId?: string;
    facilityTypeId?: string;
    assessmentType?: 'HYGIENE' | 'COMPLIANCE';
    page?: number;
    limit?: number;
  }): Promise<UnassessedFacilitiesDto> => {
    const response = await apiClient.get<UnassessedFacilitiesResponseDto>('/api/v1/assessments/unassessed/week', {
      params,
    });
    const data = response.data.data;
    // Include pagination metadata if available from backend, otherwise use unassessedCount
    return {
      ...data,
      total: data.total ?? data.unassessedCount,
      page: data.page ?? params?.page ?? 1,
      limit: data.limit ?? params?.limit ?? 10,
    };
  },

  exportAssessmentsExcel: async (params?: {
    locationId?: string;
    facilityTypeId?: string;
    assessmentType?: 'HYGIENE' | 'COMPLIANCE';
    weekNumber?: number;
    year?: number;
    startDate?: string;
    endDate?: string;
    userId?: string;
    userIds?: string[];
    facilityId?: string;
    search?: string;
  }): Promise<void> => {
    const token = localStorage.getItem('access_token');
    const { userIds, ...rest } = params ?? {};
    const flat = userIds?.length ? { ...rest, userIds: userIds.join(',') } : rest;
    const query = new URLSearchParams(
      Object.fromEntries(Object.entries(flat).filter(([, v]) => v != null)) as Record<string, string>
    ).toString();
    const response = await fetch(`${API_URL}/api/v1/assessments/export${query ? `?${query}` : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    const filename =
      response.headers.get('Content-Disposition')?.match(/filename="(.+?)"/)?.[1] ?? 'assessments.xlsx';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  exportUnassessedExcel: async (params?: {
    locationId?: string;
    facilityTypeId?: string;
    assessmentType?: 'HYGIENE' | 'COMPLIANCE';
    weekNumber?: number;
    year?: number;
    startDate?: string;
    endDate?: string;
  }): Promise<void> => {
    const token = localStorage.getItem('access_token');
    const query = new URLSearchParams(
      Object.fromEntries(Object.entries(params ?? {}).filter(([, v]) => v != null)) as Record<string, string>
    ).toString();
    const response = await fetch(`${API_URL}/api/v1/assessments/unassessed/week/export${query ? `?${query}` : ''}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    const filename =
      response.headers.get('Content-Disposition')?.match(/filename="(.+?)"/)?.[1] ?? 'unassessed-facilities.xlsx';
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },

  getPreviousAssessment: async (facilityId: string): Promise<AssessmentDto | null> => {
    const response = await apiClient.get<AssessmentResponseDto>(`/api/v1/assessments/facility/${facilityId}/previous`);
    return response.data.data;
  },

  getPendingRecommendations: async (
    facilityId: string,
    params?: { assessmentType?: 'HYGIENE' | 'COMPLIANCE' }
  ): Promise<PendingRecommendationsDto> => {
    const response = await apiClient.get<{ success: boolean; data: PendingRecommendationsDto }>(
      `/api/v1/assessments/facilities/${facilityId}/pending-recommendations`,
      { params }
    );
    const data = response.data.data;
    return (
      data ?? {
        assessmentId: null,
        assessmentType: '',
        assessedAt: '',
        recommendations: [],
      }
    );
  },

  updateRecommendationResolved: async (
    recommendationId: string,
    resolved: boolean
  ): Promise<AssessmentRecommendationDto> => {
    const response = await apiClient.patch<{ success: boolean; data: AssessmentRecommendationDto }>(
      `/api/v1/assessments/recommendations/${recommendationId}`,
      { resolved }
    );
    return response.data.data;
  },

  getFines: async (params?: {
    assessmentType?: 'HYGIENE' | 'COMPLIANCE';
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<FinesResponseDto> => {
    const response = await apiClient.get<{ success: boolean; data: AssessmentFineDto[]; pagination: any; stats?: any }>(
      '/api/v1/assessments/fines',
      { params },
    );
    return { data: response.data.data ?? [], pagination: response.data.pagination, stats: response.data.stats };
  },

  markFineCollected: async (id: string, collected: boolean): Promise<AssessmentFineDto> => {
    const response = await apiClient.patch<{ success: boolean; data: AssessmentFineDto }>(
      `/api/v1/assessments/${id}/fine-collected`,
      { collected },
    );
    return response.data.data;
  },

  getFlagged: async (params?: {
    locationId?: string;
    assessmentType?: 'HYGIENE' | 'COMPLIANCE';
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }): Promise<FlaggedAssessmentsResponseDto> => {
    const response = await apiClient.get<FlaggedAssessmentsResponseDto>(
      '/api/v1/assessments/flagged',
      { params }
    );
    const data = response.data;
    return {
      total: data?.total ?? 0,
      page: data?.page ?? 1,
      limit: data?.limit ?? 20,
      byType: data?.byType ?? { HYGIENE: 0, COMPLIANCE: 0 },
      data: data?.data ?? [],
    };
  },

  updateFlag: async (id: string, body: { isFlagged: boolean }): Promise<AssessmentDto> => {
    const response = await apiClient.put<AssessmentResponseDto>(`/api/v1/assessments/${id}/flag`, body);
    return response.data.data;
  },

  checkQuestionnaireAvailability: async (
    facilityTypeId: string,
    type: 'HYGIENE' | 'COMPLIANCE'
  ): Promise<{ available: boolean; questionnaireId?: string }> => {
    const response = await apiClient.get<{ success: boolean; data: { available: boolean; questionnaireId?: string } }>(
      '/api/v1/assessments/questionnaire/availability',
      { params: { facilityTypeId, type } }
    );
    return response.data.data;
  },

  /** Public — no auth required. Facility staff submit via emailed link. */
  submitAppeal: async (assessmentId: string, reason: string): Promise<AssessmentAppeal> => {
    const response = await publicApiClient.post<{ success: boolean; data: AssessmentAppeal }>(
      `/api/v1/assessments/${assessmentId}/appeal`,
      { reason }
    );
    return response.data.data;
  },

  /** Public — no auth required. Returns minimal assessment details for the appeal page. */
  getPublicDetails: async (assessmentId: string): Promise<PublicAssessmentDetailsDto> => {
    const response = await publicApiClient.get<PublicAssessmentDetailsDto>(
      `/api/v1/assessments/${assessmentId}/public-details`
    );
    return response.data;
  },
};

// Appeals API (admin review)
export const appealsApi = {
  getAll: async (params?: { status?: string; page?: number; limit?: number }): Promise<{ data: AssessmentAppeal[]; total: number }> => {
    const response = await apiClient.get<{ success: boolean; data: AssessmentAppeal[]; total: number }>(
      '/api/v1/appeals',
      { params }
    );
    return { data: response.data.data, total: response.data.total };
  },
  approve: async (appealId: string, reviewerNote?: string): Promise<AssessmentAppeal> => {
    const response = await apiClient.patch<{ success: boolean; data: AssessmentAppeal }>(
      `/api/v1/appeals/${appealId}/approve`,
      { reviewerNote }
    );
    return response.data.data;
  },
  reject: async (appealId: string, rejectionReason: string): Promise<AssessmentAppeal> => {
    const response = await apiClient.patch<{ success: boolean; data: AssessmentAppeal }>(
      `/api/v1/appeals/${appealId}/reject`,
      { rejectionReason }
    );
    return response.data.data;
  },
};

// Audit Logs API (Admin only)
export const auditsApi = {
  getLogs: async (params?: { page?: number; limit?: number }) => {
    const response = await apiClient.get<AuditLogsResponseDto>('/api/v1/audits', { params });
    return response.data.data;
  },
};

// Geo Locations API
export const geoLocationsApi = {
  getProvinces: async (): Promise<GeoLocationDto[]> => {
    const response = await apiClient.get('/api/v1/geo-locations/get-provinces');
    return response.data.data;
  },

  getAddressInfo: async (parentId: string) => {
    const response = await apiClient.get(`/api/v1/geo-locations/get-address-info/${parentId}`);
    return response.data;
  },

  getSubLocations: async (parentId: string): Promise<GeoLocationDto[]> => {
    const response = await apiClient.get(`/api/v1/geo-locations/get-sub-locations/${parentId}`);
    return response.data.data;
  },
};

export const facilityTypesApi = {
  getAll: async (): Promise<FacilityTypeDto[]> => {
    const response = await apiClient.get('/api/v1/facilities/types', {
      params: { _t: Date.now() }, // cache-buster to avoid stale 304 data
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });
    return response.data.data;
  },

  create: async (data: CreateFacilityTypeDto): Promise<FacilityTypeDto> => {
    const response = await apiClient.post<{ success: boolean; data: FacilityTypeDto }>(
      '/api/v1/facilities/types',
      data
    );
    return response.data.data;
  },

  update: async (id: string, data: UpdateFacilityTypeDto): Promise<FacilityTypeDto> => {
    const response = await apiClient.patch<{ success: boolean; data: FacilityTypeDto }>(
      `/api/v1/facilities/types/${id}`,
      data
    );
    return response.data.data;
  },

  softDelete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/facilities/types/${id}`);
  },

  // ─── Subcategories ─────────────────────────────────────────────────────────
  listSubcategories: async (facilityTypeId: string): Promise<FacilitySubcategoryDto[]> => {
    const response = await apiClient.get<{ success: boolean; data: FacilitySubcategoryDto[] }>(
      `/api/v1/facilities/types/${facilityTypeId}/subcategories`
    );
    return response.data.data ?? [];
  },

  createSubcategory: async (
    facilityTypeId: string,
    body: CreateFacilitySubcategoryDto
  ): Promise<FacilitySubcategoryDto> => {
    const response = await apiClient.post<{ success: boolean; data: FacilitySubcategoryDto }>(
      `/api/v1/facilities/types/${facilityTypeId}/subcategories`,
      body
    );
    return response.data.data;
  },

  updateSubcategory: async (
    subcategoryId: string,
    body: UpdateFacilitySubcategoryDto
  ): Promise<FacilitySubcategoryDto> => {
    const response = await apiClient.patch<{ success: boolean; data: FacilitySubcategoryDto }>(
      `/api/v1/facilities/types/subcategories/${subcategoryId}`,
      body
    );
    return response.data.data;
  },

  deleteSubcategory: async (subcategoryId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/facilities/types/subcategories/${subcategoryId}`);
  },
};

// Compliance API (questions by facility type)
export const complianceApi = {
  getQuestionsByFacilityType: async (facilityTypeId: string): Promise<ComplianceQuestionDto[]> => {
    const response = await apiClient.get<{ success: boolean; data: ComplianceQuestionDto[] }>(
      `/api/v1/compliance/questions/facility-type/${facilityTypeId}`
    );
    return response.data.data ?? [];
  },

  getCurrentQuestionnaire: async (facilityTypeId: string, facilityId?: string): Promise<CurrentComplianceQuestionnaireResponseDto> => {
    let legacyResult: CurrentComplianceQuestionnaireResponseDto | null = null;
    const facilityParams = facilityId ? { facilityId } : {};

    // Prefer legacy single round-trip when it returns data (faster first paint).
    try {
      const response = await apiClient.get<unknown>(
        `/api/v1/compliance/questionnaire/facility-type/${facilityTypeId}/current`,
        { params: facilityParams }
      );
      const parsed = parseCurrentComplianceQuestionnaireResponse(response.data);
      const questions = normalizeComplianceQuestionTree(parsed.questions ?? []);
      legacyResult = {
        questionnaire: parsed.questionnaire,
        sections: normalizeQuestionnaireSections(parsed.sections ?? []),
        questions,
      };
      if (questions.length > 0 || parsed.questionnaire) {
        return legacyResult;
      }
    } catch {
      // fall through
    }

    // New questionnaire system — list then detail (section ordering, richer tree).
    try {
      const listResp = await apiClient.get<{ success: boolean; data: ComplianceQuestionnaireListItemDto[] }>(
        `/api/v1/questions/compliance/questionnaires`,
        { params: { facilityTypeId } }
      );
      const currentItem = (listResp.data.data ?? []).find(q => q.isCurrent);
      if (currentItem) {
        const detailResp = await apiClient.get<{ success: boolean; data: ComplianceQuestionnaireDetailDto }>(
          `/api/v1/questions/compliance/questionnaires/${currentItem.id}`
        );
        const detail = detailResp.data.data;
        return {
          questionnaire: currentItem,
          sections: normalizeQuestionnaireSections(detail.sections ?? []),
          questions: normalizeComplianceQuestionTree(detail.questions ?? []),
        };
      }
    } catch {
      // fall through
    }

    if (legacyResult) return legacyResult;

    try {
      const response = await apiClient.get<unknown>(
        `/api/v1/compliance/questionnaire/facility-type/${facilityTypeId}/current`,
        { params: facilityParams }
      );
      const parsed = parseCurrentComplianceQuestionnaireResponse(response.data);
      return {
        questionnaire: parsed.questionnaire,
        sections: normalizeQuestionnaireSections(parsed.sections ?? []),
        questions: normalizeComplianceQuestionTree(parsed.questions ?? []),
      };
    } catch {
      return { questionnaire: null, sections: [], questions: [] };
    }
  },

  getSubQuestionsForOption: async (questionId: string, selectedOptionId: string): Promise<ComplianceQuestionTreeDto[]> => {
    const response = await apiClient.get<{ success: boolean; data: ComplianceQuestionTreeDto[] }>(
      `/api/v1/compliance/questions/${questionId}/sub-questions?selectedOptionId=${encodeURIComponent(selectedOptionId)}`
    );
    return normalizeComplianceQuestionTree(response.data.data ?? []);
  },

  getChecklistStatus: (facilityId: string): Promise<ComplianceChecklistStatusDto> =>
    apiClient.get(`/api/v1/compliance/checklist/facility/${facilityId}/status`).then(r => r.data.data),
};

// Question Management API (Admin only) — base path /api/v1/questions
const questionsBase = '/api/v1/questions';

export const questionsApi = {
  // ─── Hygiene — Questionnaires ─────────────────────────────────────────────
  listHygieneQuestionnaires: async (facilityTypeId?: string): Promise<HygieneQuestionnaireListItemDto[]> => {
    const response = await apiClient.get<{ success: boolean; data: HygieneQuestionnaireListItemDto[] }>(
      `${questionsBase}/hygiene/questionnaires`,
      { params: facilityTypeId ? { facilityTypeId } : undefined }
    );
    return response.data.data ?? [];
  },

  getHygieneQuestionnaire: async (questionnaireId: string): Promise<HygieneQuestionnaireDetailDto> => {
    const response = await apiClient.get<{ success: boolean; data: HygieneQuestionnaireDetailDto }>(
      `${questionsBase}/hygiene/questionnaires/${questionnaireId}`
    );
    return response.data.data;
  },

  createHygieneQuestionnaire: async (body: CreateQuestionnaireDto): Promise<HygieneQuestionnaireListItemDto> => {
    const response = await apiClient.post<{ success: boolean; data: HygieneQuestionnaireListItemDto }>(
      `${questionsBase}/hygiene/questionnaires`,
      body
    );
    return response.data.data;
  },

  updateHygieneQuestionnaire: async (
    questionnaireId: string,
    body: UpdateQuestionnaireDto
  ): Promise<HygieneQuestionnaireDetailDto> => {
    const response = await apiClient.patch<{ success: boolean; data: HygieneQuestionnaireDetailDto }>(
      `${questionsBase}/hygiene/questionnaires/${questionnaireId}`,
      body
    );
    return response.data.data;
  },

  deleteHygieneQuestionnaire: async (questionnaireId: string): Promise<{ deleted: boolean }> => {
    const response = await apiClient.delete<{ deleted: boolean }>(
      `${questionsBase}/hygiene/questionnaires/${questionnaireId}`
    );
    return response.data;
  },

  /** Shared section CRUD — hygiene & compliance (integration guide) */
  createQuestionnaireSection: async (
    questionnaireId: string,
    body: CreateQuestionnaireSectionDto
  ): Promise<QuestionnaireSectionDto> => {
    const response = await apiClient.post<{ success: boolean; data: QuestionnaireSectionDto }>(
      `${questionsBase}/questionnaires/${questionnaireId}/sections`,
      body
    );
    return response.data.data;
  },

  updateQuestionnaireSection: async (
    sectionId: string,
    body: UpdateQuestionnaireSectionDto
  ): Promise<QuestionnaireSectionDto> => {
    const response = await apiClient.patch<{ success: boolean; data: QuestionnaireSectionDto }>(
      `${questionsBase}/sections/${sectionId}`,
      body
    );
    return response.data.data;
  },

  deleteQuestionnaireSection: async (sectionId: string): Promise<{ deleted: boolean }> => {
    const response = await apiClient.delete<{ success: boolean; data: { deleted: boolean } }>(
      `${questionsBase}/sections/${sectionId}`
    );
    return response.data.data;
  },

  // ─── Hygiene — Questions ──────────────────────────────────────────────────
  createHygieneQuestion: async (
    questionnaireId: string,
    body: CreateHygieneQuestionDto
  ): Promise<HygieneQuestionDto> => {
    const response = await apiClient.post<{ success: boolean; data: HygieneQuestionDto }>(
      `${questionsBase}/hygiene/questionnaires/${questionnaireId}/questions`,
      body
    );
    return response.data.data;
  },

  updateHygieneQuestion: async (questionId: string, body: UpdateHygieneQuestionDto): Promise<HygieneQuestionDto> => {
    const response = await apiClient.patch<{ success: boolean; data: HygieneQuestionDto }>(
      `${questionsBase}/hygiene/questions/${questionId}`,
      body
    );
    return response.data.data;
  },

  deleteHygieneQuestion: async (questionId: string): Promise<{ deleted: boolean }> => {
    const response = await apiClient.delete<{ deleted: boolean }>(
      `${questionsBase}/hygiene/questions/${questionId}`
    );
    return response.data;
  },

  // ─── Hygiene — Options ────────────────────────────────────────────────────
  createHygieneOption: async (
    questionId: string,
    body: CreateHygieneOptionDto
  ): Promise<HygieneOptionDto> => {
    const response = await apiClient.post<{ success: boolean; data: HygieneOptionDto }>(
      `${questionsBase}/hygiene/questions/${questionId}/options`,
      body
    );
    return response.data.data;
  },

  updateHygieneOption: async (optionId: string, body: UpdateHygieneOptionDto): Promise<HygieneOptionDto> => {
    const response = await apiClient.patch<{ success: boolean; data: HygieneOptionDto }>(
      `${questionsBase}/hygiene/options/${optionId}`,
      body
    );
    return response.data.data;
  },

  deleteHygieneOption: async (optionId: string): Promise<{ deleted: boolean }> => {
    const response = await apiClient.delete<{ deleted: boolean }>(
      `${questionsBase}/hygiene/options/${optionId}`
    );
    return response.data;
  },

  // ─── Compliance — Questionnaires ─────────────────────────────────────────
  listComplianceQuestionnaires: async (facilityTypeId?: string): Promise<ComplianceQuestionnaireListItemDto[]> => {
    const response = await apiClient.get<{ success: boolean; data: ComplianceQuestionnaireListItemDto[] }>(
      `${questionsBase}/compliance/questionnaires`,
      { params: facilityTypeId ? { facilityTypeId } : undefined }
    );
    return response.data.data ?? [];
  },

  getComplianceQuestionnaire: async (questionnaireId: string): Promise<ComplianceQuestionnaireDetailDto> => {
    const response = await apiClient.get<{ success: boolean; data: ComplianceQuestionnaireDetailDto }>(
      `${questionsBase}/compliance/questionnaires/${questionnaireId}`
    );
    return response.data.data;
  },

  createComplianceQuestionnaire: async (body: CreateComplianceQuestionnaireDto): Promise<ComplianceQuestionnaireListItemDto> => {
    const response = await apiClient.post<{ success: boolean; data: ComplianceQuestionnaireListItemDto }>(
      `${questionsBase}/compliance/questionnaires`,
      body
    );
    return response.data.data;
  },

  updateComplianceQuestionnaire: async (
    questionnaireId: string,
    body: UpdateComplianceQuestionnaireDto
  ): Promise<ComplianceQuestionnaireDetailDto> => {
    const response = await apiClient.patch<{ success: boolean; data: ComplianceQuestionnaireDetailDto }>(
      `${questionsBase}/compliance/questionnaires/${questionnaireId}`,
      body
    );
    return response.data.data;
  },

  deleteComplianceQuestionnaire: async (questionnaireId: string): Promise<{ deleted: boolean }> => {
    const response = await apiClient.delete<{ deleted: boolean }>(
      `${questionsBase}/compliance/questionnaires/${questionnaireId}`
    );
    return response.data;
  },

  createComplianceQuestionInQuestionnaire: async (
    questionnaireId: string,
    body: CreateComplianceQuestionInQuestionnaireDto
  ): Promise<ComplianceQuestionTreeDto> => {
    const response = await apiClient.post<{ success: boolean; data: ComplianceQuestionTreeDto }>(
      `${questionsBase}/compliance/questionnaires/${questionnaireId}/questions`,
      body
    );
    return response.data.data;
  },

  // ─── Compliance — Questions ───────────────────────────────────────────────
  listComplianceQuestions: async (
    facilityTypeId: string
  ): Promise<ComplianceQuestionManagementDto[]> => {
    const response = await apiClient.get<{ success: boolean; data: ComplianceQuestionManagementDto[] }>(
      `${questionsBase}/compliance/facility-types/${facilityTypeId}`
    );
    return response.data.data ?? [];
  },

  createComplianceQuestion: async (
    body: CreateComplianceQuestionDto
  ): Promise<ComplianceQuestionManagementDto> => {
    const response = await apiClient.post<{ success: boolean; data: ComplianceQuestionManagementDto }>(
      `${questionsBase}/compliance/questions`,
      body
    );
    return response.data.data;
  },

  updateComplianceQuestion: async (
    questionId: string,
    body: UpdateComplianceQuestionDto
  ): Promise<ComplianceQuestionManagementDto> => {
    const response = await apiClient.patch<{ success: boolean; data: ComplianceQuestionManagementDto }>(
      `${questionsBase}/compliance/questions/${questionId}`,
      body
    );
    return response.data.data;
  },

  deleteComplianceQuestion: async (questionId: string): Promise<{ deleted: boolean }> => {
    const response = await apiClient.delete<{ deleted: boolean }>(
      `${questionsBase}/compliance/questions/${questionId}`
    );
    return response.data;
  },

  // ─── Compliance — Options ──────────────────────────────────────────────────
  createComplianceOption: async (
    questionId: string,
    body: CreateComplianceOptionDto
  ): Promise<ComplianceQuestionOptionDto> => {
    const response = await apiClient.post<{ success: boolean; data: ComplianceQuestionOptionDto }>(
      `${questionsBase}/compliance/questions/${questionId}/options`,
      body
    );
    return response.data.data;
  },

  updateComplianceOption: async (
    optionId: string,
    body: UpdateComplianceOptionDto
  ): Promise<ComplianceQuestionOptionDto> => {
    const response = await apiClient.patch<{ success: boolean; data: ComplianceQuestionOptionDto }>(
      `${questionsBase}/compliance/options/${optionId}`,
      body
    );
    return response.data.data;
  },

  deleteComplianceOption: async (optionId: string): Promise<{ deleted: boolean }> => {
    const response = await apiClient.delete<{ deleted: boolean }>(
      `${questionsBase}/compliance/options/${optionId}`
    );
    return response.data;
  },

  // ─── Sub-questions on Options ─────────────────────────────────────────────
  createHygieneSubQuestion: async (
    optionId: string,
    body: CreateHygieneQuestionDto
  ): Promise<HygieneQuestionDto> => {
    const response = await apiClient.post<{ success: boolean; data: HygieneQuestionDto }>(
      `${questionsBase}/hygiene/options/${optionId}/subquestions`,
      body
    );
    return response.data.data;
  },

  createComplianceSubQuestion: async (
    optionId: string,
    body: CreateComplianceQuestionInQuestionnaireDto
  ): Promise<ComplianceQuestionTreeDto> => {
    const response = await apiClient.post<{ success: boolean; data: ComplianceQuestionTreeDto }>(
      `${questionsBase}/compliance/options/${optionId}/subquestions`,
      body
    );
    return response.data.data;
  },

  // ─── Copy Questionnaire ───────────────────────────────────────────────────
  copyQuestionnaire: async (
    questionnaireId: string,
    body: CopyQuestionnaireDto
  ): Promise<HygieneQuestionnaireListItemDto> => {
    const response = await apiClient.post<{ success: boolean; data: HygieneQuestionnaireListItemDto }>(
      `${questionsBase}/questionnaires/${questionnaireId}/copy`,
      body
    );
    return response.data.data;
  },

  /** Whether an active checklist exists for new assessments for this facility type. */
  checkChecklistAvailability: async (
    facilityTypeId: string,
    type: 'HYGIENE' | 'COMPLIANCE'
  ): Promise<{ available: boolean; questionnaire?: { id: string; name: string } | null }> => {
    const response = await apiClient.get<{
      success: boolean;
      data: { available: boolean; questionnaire?: { id: string; name: string } | null };
    }>(`${questionsBase}/checklist/availability`, { params: { facilityTypeId, type } });
    return response.data.data;
  },

  // ─── Reference Documents (shared by hygiene & compliance questionnaires) ──
  listQuestionnaireDocuments: async (questionnaireId: string): Promise<QuestionnaireDocumentDto[]> => {
    const response = await apiClient.get<{ success: boolean; data: QuestionnaireDocumentDto[] }>(
      `${questionsBase}/questionnaires/${questionnaireId}/documents`
    );
    return response.data.data ?? [];
  },

  uploadQuestionnaireDocument: async (
    questionnaireId: string,
    file: File,
    label?: string
  ): Promise<QuestionnaireDocumentDto> => {
    const formData = new FormData();
    formData.append('file', file);
    if (label) formData.append('label', label);
    const response = await apiClient.post<{ success: boolean; data: QuestionnaireDocumentDto }>(
      `${questionsBase}/questionnaires/${questionnaireId}/documents`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  },

  deleteQuestionnaireDocument: async (documentId: string): Promise<void> => {
    await apiClient.delete(`${questionsBase}/questionnaires/documents/${documentId}`);
  },

  // ─── Beverage Inspections — Questionnaires (admin builder, PRE_EVENT/IN_EVENT/POST_EVENT) ──
  listBeverageInspectionQuestionnaires: async (
    phase?: BeverageInspectionPhase
  ): Promise<BeverageInspectionQuestionnaireListItemDto[]> => {
    const response = await apiClient.get<{ success: boolean; data: BeverageInspectionQuestionnaireListItemDto[] }>(
      beverageQuestionsBase,
      { params: phase ? { phase } : undefined }
    );
    return response.data.data ?? [];
  },

  getBeverageInspectionQuestionnaire: async (questionnaireId: string): Promise<BeverageInspectionQuestionnaireDetailDto> => {
    const response = await apiClient.get<{ success: boolean; data: BeverageInspectionQuestionnaireDetailDto }>(
      `${beverageQuestionsBase}/${questionnaireId}`
    );
    return response.data.data;
  },

  createBeverageInspectionQuestionnaire: async (
    body: CreateBeverageInspectionQuestionnaireDto
  ): Promise<BeverageInspectionQuestionnaireListItemDto> => {
    const response = await apiClient.post<{ success: boolean; data: BeverageInspectionQuestionnaireListItemDto }>(
      beverageQuestionsBase,
      body
    );
    return response.data.data;
  },

  updateBeverageInspectionQuestionnaire: async (
    questionnaireId: string,
    body: UpdateBeverageInspectionQuestionnaireDto
  ): Promise<BeverageInspectionQuestionnaireDetailDto> => {
    const response = await apiClient.patch<{ success: boolean; data: BeverageInspectionQuestionnaireDetailDto }>(
      `${beverageQuestionsBase}/${questionnaireId}`,
      body
    );
    return response.data.data;
  },

  deleteBeverageInspectionQuestionnaire: async (questionnaireId: string): Promise<{ deleted: boolean }> => {
    const response = await apiClient.delete<{ deleted: boolean }>(`${beverageQuestionsBase}/${questionnaireId}`);
    return response.data;
  },

  copyBeverageInspectionQuestionnaire: async (
    questionnaireId: string,
    body: CopyBeverageInspectionQuestionnaireDto
  ): Promise<BeverageInspectionQuestionnaireListItemDto> => {
    const response = await apiClient.post<{ success: boolean; data: BeverageInspectionQuestionnaireListItemDto }>(
      `${beverageQuestionsBase}/${questionnaireId}/copy`,
      body
    );
    return response.data.data;
  },

  createBeverageInspectionSection: async (
    questionnaireId: string,
    body: CreateQuestionnaireSectionDto
  ): Promise<QuestionnaireSectionDto> => {
    const response = await apiClient.post<{ success: boolean; data: QuestionnaireSectionDto }>(
      `${beverageQuestionsBase}/${questionnaireId}/sections`,
      body
    );
    return response.data.data;
  },

  updateBeverageInspectionSection: async (
    sectionId: string,
    body: UpdateQuestionnaireSectionDto
  ): Promise<QuestionnaireSectionDto> => {
    const response = await apiClient.patch<{ success: boolean; data: QuestionnaireSectionDto }>(
      `/api/v1/beverage-sections/${sectionId}`,
      body
    );
    return response.data.data;
  },

  deleteBeverageInspectionSection: async (sectionId: string): Promise<{ deleted: boolean }> => {
    const response = await apiClient.delete<{ deleted: boolean }>(`/api/v1/beverage-sections/${sectionId}`);
    return response.data;
  },

  createBeverageInspectionQuestionInQuestionnaire: async (
    questionnaireId: string,
    body: CreateBeverageInspectionQuestionForQuestionnaireDto
  ): Promise<BeverageQuestionTreeDto> => {
    const response = await apiClient.post<{ success: boolean; data: BeverageQuestionTreeDto }>(
      `${beverageQuestionsBase}/${questionnaireId}/questions`,
      body
    );
    return response.data.data;
  },

  createBeverageInspectionQuestion: async (
    body: CreateBeverageInspectionQuestionDto
  ): Promise<BeverageQuestionTreeDto> => {
    const response = await apiClient.post<{ success: boolean; data: BeverageQuestionTreeDto }>(
      `/api/v1/beverage-questions`,
      body
    );
    return response.data.data;
  },

  updateBeverageInspectionQuestion: async (
    questionId: string,
    body: UpdateBeverageInspectionQuestionDto
  ): Promise<BeverageQuestionTreeDto> => {
    const response = await apiClient.patch<{ success: boolean; data: BeverageQuestionTreeDto }>(
      `/api/v1/beverage-questions/${questionId}`,
      body
    );
    return response.data.data;
  },

  deleteBeverageInspectionQuestion: async (questionId: string): Promise<{ deleted: boolean }> => {
    const response = await apiClient.delete<{ deleted: boolean }>(`/api/v1/beverage-questions/${questionId}`);
    return response.data;
  },

  createBeverageInspectionOption: async (
    questionId: string,
    body: CreateBeverageInspectionOptionDto
  ): Promise<BeverageQuestionOptionDto> => {
    const response = await apiClient.post<{ success: boolean; data: BeverageQuestionOptionDto }>(
      `/api/v1/beverage-questions/${questionId}/options`,
      body
    );
    return response.data.data;
  },

  updateBeverageInspectionOption: async (
    optionId: string,
    body: UpdateBeverageInspectionOptionDto
  ): Promise<BeverageQuestionOptionDto> => {
    const response = await apiClient.patch<{ success: boolean; data: BeverageQuestionOptionDto }>(
      `/api/v1/beverage-options/${optionId}`,
      body
    );
    return response.data.data;
  },

  deleteBeverageInspectionOption: async (optionId: string): Promise<{ deleted: boolean }> => {
    const response = await apiClient.delete<{ deleted: boolean }>(`/api/v1/beverage-options/${optionId}`);
    return response.data;
  },

  createBeverageInspectionSubQuestion: async (
    optionId: string,
    body: CreateBeverageInspectionSubQuestionDto
  ): Promise<BeverageQuestionTreeDto> => {
    const response = await apiClient.post<{ success: boolean; data: BeverageQuestionTreeDto }>(
      `/api/v1/beverage-options/${optionId}/subquestions`,
      body
    );
    return response.data.data;
  },

  importBeverageInspectionQuestionnaire: async (file: File, phase?: BeverageInspectionPhase): Promise<BeverageInspectionQuestionnaireDetailDto> => {
    const formData = new FormData();
    formData.append('file', file);
    if (phase) formData.append('phase', phase);
    const response = await apiClient.post<{ success: boolean; data: BeverageInspectionQuestionnaireDetailDto }>(
      `${beverageQuestionsBase}/import`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  },

  exportBeverageInspectionQuestionnaire: async (questionnaireId: string): Promise<Blob> => {
    const response = await apiClient.get(`${beverageQuestionsBase}/${questionnaireId}/export`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },

  listBeverageInspectionQuestionnaireDocuments: async (questionnaireId: string): Promise<QuestionnaireDocumentDto[]> => {
    const response = await apiClient.get<{ success: boolean; data: QuestionnaireDocumentDto[] }>(
      `${beverageQuestionsBase}/${questionnaireId}/documents`
    );
    return response.data.data ?? [];
  },

  uploadBeverageInspectionQuestionnaireDocument: async (
    questionnaireId: string,
    file: File,
    label?: string
  ): Promise<QuestionnaireDocumentDto> => {
    const formData = new FormData();
    formData.append('file', file);
    if (label) formData.append('label', label);
    const response = await apiClient.post<{ success: boolean; data: QuestionnaireDocumentDto }>(
      `${beverageQuestionsBase}/${questionnaireId}/documents`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  },

  deleteBeverageInspectionQuestionnaireDocument: async (documentId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/beverage-documents/${documentId}`);
  },
};

// Questionnaires API
export const questionnairesApi = {
  getByFacilityId: async (facilityId: string): Promise<QuestionnaireDto> => {
    const response = await apiClient.get(`/api/v1/assessments/facility/${facilityId}/questionnaire`);
    return response.data.data;
  },

  getByFacilityTypeId: async (facilityTypeId: string): Promise<QuestionnaireDto> => {
    const response = await apiClient.get(`/api/v1/assessments/questionnaire/${facilityTypeId}`);
    return response.data.data;
  },

  getSubquestionsByOption: async (optionId: string): Promise<QuestionDto[]> => {
    const response = await apiClient.get(`/api/v1/assessments/subquestions/option/${optionId}`);
    return response.data.data || [];
  },

  /** Reference PDFs for a questionnaire — used by inspectors during on-site assessment. */
  getReferenceDocuments: async (questionnaireId: string): Promise<QuestionnaireDocumentDto[]> => {
    const response = await apiClient.get(`/api/v1/assessments/questionnaire/${questionnaireId}/documents`);
    return response.data.data || [];
  },
};

// Reports API
export const reportsApi = {
  downloadAssessmentPDF: (id: string): Promise<Blob> =>
    apiClient.get(`/api/v1/reports/assessment/${id}`, { responseType: 'blob' }).then(r => r.data),

  downloadFacilityPDF: (facilityId: string, params?: { startDate?: string; endDate?: string }): Promise<Blob> =>
    apiClient.get(`/api/v1/reports/facility/${facilityId}`, { params, responseType: 'blob' }).then(r => r.data),

  shareAssessmentPDF: (id: string): Promise<{ sent: boolean; to: string }> =>
    apiClient.post(`/api/v1/reports/assessment/${id}/share`).then(r => r.data.data),
};

// Analytics API
export const analyticsApi = {
  getAnalytics: async () => {
    const response = await apiClient.get('/api/v1/analytics');
    return response.data.data || response.data;
  },

  getLocationCleanliness: async (params?: { startDate?: string; endDate?: string; facilityTypeId?: string }) => {
    const response = await apiClient.get('/api/v1/analytics/location-cleanliness', { params });
    return response.data.data || response.data;
  },

  getFacilityTypeMetrics: async (params?: { startDate?: string; endDate?: string; facilityTypeId?: string }) => {
    const response = await apiClient.get('/api/v1/analytics/facility-type-metrics', { params });
    return response.data.data || response.data;
  },

  getMapData: async (params?: { startDate?: string; endDate?: string; facilityTypeId?: string }) => {
    const response = await apiClient.get('/api/v1/analytics/map-data', { params });
    return response.data.data || response.data;
  },

  getTrends: async (params?: { startDate?: string; endDate?: string; facilityTypeId?: string }) => {
    const response = await apiClient.get('/api/v1/analytics/trends', { params });
    return response.data.data || response.data;
  },

  getQuestionnaireAnalytics: async (
    questionnaireId: string,
    params?: {
      locationId?: string;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<QuestionnaireAnalyticsDto> => {
    const response = await apiClient.get<QuestionnaireAnalyticsResponseDto>(
      `/api/v1/analytics/questionnaire/${questionnaireId}`,
      { params }
    );
    return response.data.data;
  },

  getFacilityQuestionnaireResponses: async (
    questionnaireId: string,
    params?: {
      locationId?: string;
      startDate?: string;
      endDate?: string;
    }
  ): Promise<FacilityQuestionnaireResponsesDto> => {
    const response = await apiClient.get<FacilityQuestionnaireResponsesApiDto>(
      `/api/v1/analytics/questionnaire/${questionnaireId}/facility-responses`,
      { params }
    );
    return response.data.data;
  },

  getQuestionFacilityResponses: async (
    questionnaireId: string,
    questionId: string,
    params?: { locationId?: string; startDate?: string; endDate?: string },
  ): Promise<{
    questionnaireId: string;
    questionnaireName: string;
    questionId: string;
    questionText: string;
    facilityTypeName: string;
    optionResponses: Array<{
      optionText: string;
      count: number;
      facilities: Array<{ facilityId: string; facilityName: string; locationName: string; assessmentDate: string; score: number }>;
    }>;
  }> => {
    const response = await apiClient.get(
      `/api/v1/analytics/questionnaire/${questionnaireId}/questions/${questionId}/facility-responses`,
      { params },
    );
    return response.data.data;
  },

  getLocationRanking: async (params?: {
    locationType?: string;
    parentLocationId?: string;
    weekNumber?: number;
    year?: number;
    startDate?: string;
    endDate?: string;
    facilityTypeId?: string;
  }) => {
    const response = await apiClient.get('/api/v1/analytics/location-ranking', { params });
    return response.data.data || response.data;
  },

  getLocationPerformance: async (params?: {
    parentLocationId?: string;
    showFacilities?: boolean;
    locationType?: string;
    startDate?: string;
    endDate?: string;
    weekNumber?: number;
    year?: number;
    facilityTypeId?: string;
  }) => {
    const response = await apiClient.get('/api/v1/analytics/location-performance', { params });
    return response.data.data || response.data;
  },

  getLocationActivity: async (params?: {
    locationType?: string;
    parentLocationId?: string;
    numberOfWeeks?: number;
    facilityTypeId?: string;
  }) => {
    const response = await apiClient.get('/api/v1/analytics/location-activity', { params });
    return response.data.data || response.data;
  },

  getHygieneTrend: async (params?: { numberOfWeeks?: number; facilityTypeId?: string; startDate?: string; endDate?: string }) => {
    const response = await apiClient.get<{ success: boolean; data: HygieneTrendPointDto[] }>(
      '/api/v1/analytics/hygiene/trend',
      { params }
    );
    return response.data.data ?? [];
  },

  getHygieneScoreDistribution: async (params?: {
    weekNumber?: number;
    year?: number;
    facilityTypeId?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const response = await apiClient.get<{ success: boolean; data: ScoreDistributionBucketDto[] }>(
      '/api/v1/analytics/hygiene/score-distribution',
      { params }
    );
    return response.data.data ?? [];
  },

  getHygieneByFacilityType: async (params?: { weekNumber?: number; year?: number; startDate?: string; endDate?: string }) => {
    const response = await apiClient.get<{ success: boolean; data: HygieneByFacilityTypeDto[] }>(
      '/api/v1/analytics/hygiene/by-facility-type',
      { params }
    );
    return response.data.data ?? [];
  },

  getComplianceTrend: async (params?: { numberOfWeeks?: number; facilityTypeId?: string; startDate?: string; endDate?: string }) => {
    const response = await apiClient.get<{ success: boolean; data: HygieneTrendPointDto[] }>(
      '/api/v1/analytics/compliance/trend',
      { params }
    );
    return response.data.data ?? [];
  },

  getComplianceScoreDistribution: async (params?: {
    weekNumber?: number;
    year?: number;
    facilityTypeId?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const response = await apiClient.get<{ success: boolean; data: ScoreDistributionBucketDto[] }>(
      '/api/v1/analytics/compliance/score-distribution',
      { params }
    );
    return response.data.data ?? [];
  },

  getComplianceByFacilityType: async (params?: { weekNumber?: number; year?: number; startDate?: string; endDate?: string }) => {
    const response = await apiClient.get<{ success: boolean; data: HygieneByFacilityTypeDto[] }>(
      '/api/v1/analytics/compliance/by-facility-type',
      { params }
    );
    return response.data.data ?? [];
  },

  getComplianceQuestionBreakdown: async (params?: {
    facilityTypeId?: string;
    weekNumber?: number;
    year?: number;
    startDate?: string;
    endDate?: string;
  }) => {
    const response = await apiClient.get<{ success: boolean; data: ComplianceQuestionBreakdownDto[] }>(
      '/api/v1/analytics/compliance/question-breakdown',
      { params }
    );
    return response.data.data ?? [];
  },

  getComplianceFacilityQuestionnaireResponses: async (
    facilityTypeId: string,
    params?: { startDate?: string; endDate?: string }
  ): Promise<FacilityQuestionnaireResponsesDto> => {
    const response = await apiClient.get<FacilityQuestionnaireResponsesApiDto>(
      '/api/v1/analytics/compliance/facility-responses',
      { params: { facilityTypeId, ...params } }
    );
    return response.data.data;
  },

  // New endpoints
  getHygieneSummary: async (params?: {
    startDate?: string;
    endDate?: string;
    facilityTypeId?: string;
  }): Promise<AssessmentSummaryDto> => {
    const response = await apiClient.get<{ success: boolean; data: AssessmentSummaryDto }>(
      '/api/v1/analytics/hygiene/summary',
      { params }
    );
    return response.data.data;
  },

  getComplianceSummary: async (params?: {
    startDate?: string;
    endDate?: string;
    facilityTypeId?: string;
  }): Promise<AssessmentSummaryDto> => {
    const response = await apiClient.get<{ success: boolean; data: AssessmentSummaryDto }>(
      '/api/v1/analytics/compliance/summary',
      { params }
    );
    return response.data.data;
  },

  getDecisions: async (params?: {
    startDate?: string;
    endDate?: string;
    assessmentType?: 'HYGIENE' | 'COMPLIANCE';
  }): Promise<DecisionsAnalyticsDto> => {
    const response = await apiClient.get<{ success: boolean; data: DecisionsAnalyticsDto }>(
      '/api/v1/analytics/decisions',
      { params }
    );
    return response.data.data;
  },

  getFollowup: async (params?: {
    startDate?: string;
    endDate?: string;
    assessmentType?: 'HYGIENE' | 'COMPLIANCE';
  }): Promise<FollowupAnalyticsDto> => {
    const response = await apiClient.get<{ success: boolean; data: FollowupAnalyticsDto }>(
      '/api/v1/analytics/followup',
      { params }
    );
    return response.data.data;
  },

  getMapDataForType: async (params?: {
    assessmentType?: 'HYGIENE' | 'COMPLIANCE';
    startDate?: string;
    endDate?: string;
    facilityTypeId?: string;
  }) => {
    const response = await apiClient.get('/api/v1/analytics/map-data', { params });
    return response.data.data || response.data;
  },

  getComplianceLocationRanking: async (params?: {
    parentLocationId?: string;
    locationType?: string;
    startDate?: string;
    endDate?: string;
    facilityTypeId?: string;
    weekNumber?: number;
    year?: number;
  }) => {
    const response = await apiClient.get('/api/v1/analytics/compliance/location-ranking', { params });
    return response.data.data || response.data;
  },

  getFacilitiesRanking: async (params?: {
    assessmentType?: 'HYGIENE' | 'COMPLIANCE';
    limit?: number;
    direction?: 'top' | 'bottom';
    startDate?: string;
    endDate?: string;
    facilityTypeId?: string;
  }): Promise<FacilitiesRankingResponseDto> => {
    const response = await apiClient.get<{ success: boolean; data: FacilitiesRankingResponseDto }>(
      '/api/v1/analytics/facilities/ranking',
      { params }
    );
    return response.data.data;
  },

  getFacilityRegistration: async (params?: {
    parentLocationId?: string;
    locationType?: string;
    facilityTypeId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<FacilityRegistrationAnalyticsDto> => {
    const response = await apiClient.get<{ success: boolean; data: FacilityRegistrationAnalyticsDto }>(
      '/api/v1/analytics/facility-registration',
      { params }
    );
    return response.data.data;
  },

  getAssessmentKPIs: async (params?: {
    month?: string;
    type?: string;
    facilityTypeId?: string;
    geoLocationId?: string;
  }): Promise<import('../types/api').AssessmentKPIsDto> => {
    const response = await apiClient.get<{ success: boolean; data: import('../types/api').AssessmentKPIsDto }>(
      '/api/v1/dashboard/assessment-kpis',
      { params }
    );
    return response.data.data;
  },

  getDecisionsAnalytics: async (params?: {
    startDate?: string;
    endDate?: string;
    assessmentType?: string;
    facilityTypeId?: string;
    parentLocationId?: string;
    locationType?: string;
  }): Promise<DecisionsAnalyticsPageDto> => {
    const response = await apiClient.get<{ success: boolean; data: DecisionsAnalyticsPageDto }>(
      '/api/v1/analytics/decisions-analytics',
      { params }
    );
    return response.data.data;
  },

  exportDecisionsAnalytics: async (params?: {
    startDate?: string;
    endDate?: string;
    assessmentType?: string;
    facilityTypeId?: string;
    parentLocationId?: string;
    locationType?: string;
  }): Promise<Blob> => {
    const response = await apiClient.get('/api/v1/analytics/decisions-analytics/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  finesTrend: async (params?: {
    assessmentType?: string;
    facilityTypeId?: string;
    parentLocationId?: string;
    granularity?: 'monthly' | 'weekly';
    startDate?: string;
    endDate?: string;
  }): Promise<{ granularity: string; periods: Array<{ period: string; label: string; count: number; totalFines: number }>; totalFines: number; totalCount: number }> => {
    const response = await apiClient.get('/api/v1/analytics/fines-trend', { params });
    return response.data.data;
  },
};

// Recommendations API
export const recommendationsApi = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    isResolved?: 'true' | 'false';
    assessmentType?: string;
    facilityTypeId?: string;
    locationId?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
    overdue?: 'true' | 'false';
  }): Promise<RecommendationsListResponseDto> => {
    const response = await apiClient.get<{ success: boolean; data: RecommendationListItemDto[]; pagination: any }>(
      '/api/v1/assessments/recommendations',
      { params }
    );
    return {
      data: response.data.data ?? [],
      pagination: response.data.pagination,
    };
  },

  export: async (params?: {
    isResolved?: 'true' | 'false';
    assessmentType?: string;
    facilityTypeId?: string;
    locationId?: string;
    search?: string;
    startDate?: string;
    endDate?: string;
    overdue?: 'true' | 'false';
  }): Promise<Blob> => {
    const response = await apiClient.get('/api/v1/assessments/recommendations/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  markResolved: async (id: string, resolved: boolean): Promise<AssessmentRecommendationDto> => {
    const response = await apiClient.patch<{ success: boolean; data: AssessmentRecommendationDto }>(
      `/api/v1/assessments/recommendations/${id}`,
      { resolved }
    );
    return response.data.data;
  },
};

// Search API
export const searchApi = {
  search: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    geoLocationId?: string;
    locationType?: string;
    facilityTypeId?: string;
    facilityId?: string;
    questionnaireId?: string;
    isClean?: boolean;
    minScore?: number;
    maxScore?: number;
    hasSignature?: boolean;
    userId?: string;
    userRole?: string;
    userStatus?: string;
    startDate?: string;
    endDate?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => {
    const response = await apiClient.get('/api/v1/search', { params });
    return response.data;
  },
};

// Facility Counts API
export const facilityCountsApi = {
  getAll: async (params?: { geoLocationId?: string; facilityTypeId?: string }): Promise<FacilityCountDto[]> => {
    const response = await apiClient.get<FacilityCountsResponseDto>('/api/v1/facility-counts', { params });
    return response.data.data;
  },

  create: async (data: CreateFacilityCountDto): Promise<FacilityCountDto> => {
    const response = await apiClient.post<FacilityCountResponseDto>('/api/v1/facility-counts', data);
    return response.data.data;
  },

  update: async (id: string, data: UpdateFacilityCountDto): Promise<FacilityCountDto> => {
    const response = await apiClient.patch<FacilityCountResponseDto>(`/api/v1/facility-counts/${id}`, data);
    return response.data.data;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/facility-counts/${id}`);
  },
};

// Objective Analytics API
export const objectiveAnalyticsApi = {
  // Original objective analytics endpoint
  getObjectives: async (params?: {
    locationId?: string;
    numberOfWeeks?: number;
  }): Promise<ObjectiveAnalyticsResponseDto> => {
    const response = await apiClient.get<ObjectiveAnalyticsApiResponseDto>('/api/v1/analytics/objectives', { params });
    return response.data.data;
  },

  // NEW: Get standings/status distribution per objective
  getStandingsPerObjective: async (params: {
    objective: string;
    weekNumber?: number;
    year?: number;
    month?: string;
    startDate?: string;
    endDate?: string;
    facilityTypeId?: string;
    geoLocationId?: string;
  }): Promise<StandingsPerObjectiveDto> => {
    const response = await apiClient.get<StandingsPerObjectiveApiResponseDto>(
      '/api/v1/analytics/standings-per-objective',
      { params }
    );
    return response.data.data;
  },

  // Detailed per-facility requirement status with server-side pagination + search
  getFacilitiesRequirementStatus: async (params: {
    objective: string;
    locationId?: string;
    facilityTypeId?: string;
    weekNumber?: number;
    year?: number;
    month?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
    search?: string;
  }): Promise<FacilitiesRequirementStatusDto & { page: number; totalPages: number }> => {
    const response = await apiClient.get<FacilitiesRequirementStatusApiResponseDto>(
      '/api/v1/analytics/facilities-requirement-status',
      { params }
    );
    return response.data.data as any;
  },

  // NEW: Get location ranking by objective with drill-down capability
  getLocationRanking: async (params: {
    objective: string;
    parentLocationId?: string;
    weekNumber?: number;
    year?: number;
    facilityTypeId?: string;
  }): Promise<LocationRankingDto> => {
    const response = await apiClient.get<LocationRankingApiResponseDto>(
      '/api/v1/analytics/location-ranking-by-objective',
      { params }
    );
    return response.data.data;
  },

  /**
   * One row per facility, all 5 key objectives combined — backend-generated .xlsx.
   * Status always reflects each facility's LATEST assessment (all time); the period
   * params (week/month/date range) only select WHICH facilities appear: those
   * assessed at least once within that period.
   */
  exportAllObjectivesExcel: async (params?: {
    locationId?: string;
    facilityTypeId?: string;
    weekNumber?: number;
    year?: number;
    month?: string;
    startDate?: string;
    endDate?: string;
    search?: string;
  }): Promise<void> => {
    const response = await apiClient.get('/api/v1/analytics/facilities-requirement-status/export-all-objectives', {
      params,
      responseType: 'blob',
    });
    const filename =
      (response.headers['content-disposition'] as string | undefined)?.match(/filename="(.+?)"/)?.[1] ??
      `hygiene-objectives-report-${new Date().toISOString().split('T')[0]}.xlsx`;
    const url = URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
};

// Surveys API — generic per-jurisdiction data collection (one response per location)
export const surveysApi = {
  list: async (): Promise<SurveyDto[]> => {
    const response = await apiClient.get<{ success: boolean; data: SurveyDto[] }>('/api/v1/surveys');
    return response.data.data ?? [];
  },

  getById: async (surveyId: string): Promise<SurveyDto> => {
    const response = await apiClient.get<{ success: boolean; data: SurveyDto }>(`/api/v1/surveys/${surveyId}`);
    return response.data.data;
  },

  /** Completion rollup for drill-down: who responded / who didn't, per child location. */
  getTracking: async (surveyId: string, locationId?: string): Promise<SurveyTrackingDto> => {
    const response = await apiClient.get<{ success: boolean; data: SurveyTrackingDto }>(
      `/api/v1/surveys/${surveyId}/tracking`,
      { params: locationId ? { locationId } : undefined }
    );
    return response.data.data;
  },

  /** One location's response (or null) + the survey definition for rendering the form. */
  getResponse: async (surveyId: string, locationId: string): Promise<SurveyLocationResponseDto> => {
    const response = await apiClient.get<{ success: boolean; data: SurveyLocationResponseDto }>(
      `/api/v1/surveys/${surveyId}/responses/${locationId}`
    );
    return response.data.data;
  },

  /** Create or overwrite a location's response (previous answers are versioned server-side). */
  saveResponse: async (surveyId: string, locationId: string, answers: SurveyAnswers): Promise<SurveyResponseDto> => {
    const response = await apiClient.put<{ success: boolean; data: SurveyResponseDto }>(
      `/api/v1/surveys/${surveyId}/responses/${locationId}`,
      { answers }
    );
    return response.data.data;
  },

  /** Backend-generated .xlsx: responses + one sheet per table field + tracking sheet. */
  exportExcel: async (surveyId: string, locationId?: string): Promise<void> => {
    const response = await apiClient.get(`/api/v1/surveys/${surveyId}/export`, {
      params: locationId ? { locationId } : undefined,
      responseType: 'blob',
    });
    const filename =
      (response.headers['content-disposition'] as string | undefined)?.match(/filename="(.+?)"/)?.[1] ??
      `survey-report-${new Date().toISOString().split('T')[0]}.xlsx`;
    const url = URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
};

export const dashboardApi = {
  getOverview: async (params?: {
    month?: string;
    type?: 'HYGIENE' | 'COMPLIANCE';
    facilityTypeId?: string;
    geoLocationId?: string;
  }) => {
    const response = await apiClient.get('/api/v1/dashboard/overview', { params });
    return response.data.data || response.data;
  },

  getLocationActivityRanking: async (params?: {
    granularity?: 'daily' | 'weekly' | 'monthly';
    from?: string;
    to?: string;
    type?: 'HYGIENE' | 'COMPLIANCE';
    locationType?: string;
    parentGeoLocationId?: string;
    facilityTypeId?: string;
    limit?: number;
    page?: number;
  }) => {
    const response = await apiClient.get('/api/v1/dashboard/location-activity-ranking', { params });
    return response.data.data || response.data;
  },

  getTrends: async (params?: {
    granularity?: 'daily' | 'weekly' | 'monthly';
    from?: string;
    to?: string;
    type?: 'HYGIENE' | 'COMPLIANCE';
    facilityTypeId?: string;
    geoLocationId?: string;
  }) => {
    const response = await apiClient.get('/api/v1/dashboard/trends', { params });
    return response.data.data || response.data;
  },

  getEnforcement: async (params?: {
    month?: string;
    type?: 'HYGIENE' | 'COMPLIANCE';
    facilityTypeId?: string;
    geoLocationId?: string;
  }) => {
    const response = await apiClient.get('/api/v1/dashboard/enforcement', { params });
    return response.data.data || response.data;
  },

  getFacilityTypePerformance: async (params?: {
    month?: string;
    type?: 'HYGIENE' | 'COMPLIANCE';
    geoLocationId?: string;
  }) => {
    const response = await apiClient.get('/api/v1/dashboard/facility-type-performance', { params });
    return response.data.data || response.data;
  },

  getFollowups: async (params?: {
    month?: string;
    type?: 'HYGIENE' | 'COMPLIANCE';
    facilityTypeId?: string;
    geoLocationId?: string;
  }) => {
    const response = await apiClient.get('/api/v1/dashboard/followups', { params });
    return response.data.data || response.data;
  },

  getLocationRanking: async (params?: {
    month?: string;
    type?: 'HYGIENE' | 'COMPLIANCE';
    locationType?: string;
    facilityTypeId?: string;
    geoLocationId?: string;
    limit?: number;
  }) => {
    const response = await apiClient.get('/api/v1/dashboard/location-ranking', { params });
    return response.data.data || response.data;
  },
};

// ─── Traditional Beverage Authorization module ─────────────────────────────────

export interface BeverageAuthorizationListParams {
  page?: number;
  limit?: number;
  status?: BeverageAuthorizationStatus;
  locationId?: string;
  beverageTypeId?: string;
  purpose?: BeverageEventPurpose;
  from?: string;
  to?: string;
  reference?: string;
}

export const beverageAuthorizationsApi = {
  list: async (params?: BeverageAuthorizationListParams): Promise<BeverageAuthorizationListDto> => {
    const response = await apiClient.get<{ success: boolean; data: BeverageAuthorizationListDto }>(
      '/api/v1/beverage-authorizations',
      { params }
    );
    return response.data.data;
  },

  getById: async (id: string): Promise<BeverageAuthorizationDetailDto> => {
    const response = await apiClient.get<{ success: boolean; data: BeverageAuthorizationDetailDto }>(
      `/api/v1/beverage-authorizations/${id}`
    );
    return response.data.data;
  },

  acknowledge: async (id: string): Promise<void> => {
    await apiClient.post(`/api/v1/beverage-authorizations/${id}/acknowledge`);
  },

  /** Backend-generated export (xlsx/csv/pdf) — PII always masked server-side. */
  export: async (format: 'xlsx' | 'csv' | 'pdf', params?: BeverageAuthorizationListParams): Promise<void> => {
    const response = await apiClient.get('/api/v1/beverage-authorizations/export', {
      params: { ...params, format },
      responseType: 'blob',
    });
    const filename =
      (response.headers['content-disposition'] as string | undefined)?.match(/filename="(.+?)"/)?.[1] ??
      `beverage-authorizations-${new Date().toISOString().split('T')[0]}.${format}`;
    const url = URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  },
};

const beverageQuestionsBase = '/api/v1/beverage-questionnaires';

/** Runtime endpoints — fetching/answering a dynamic inspection questionnaire. Mirrors complianceApi,
 * minus the legacy flat-catalog fallback dance (this is a brand-new engine, no legacy catalog). */
export const beverageInspectionsApi = {
  getCurrentQuestionnaire: async (
    phase: BeverageInspectionPhase
  ): Promise<CurrentBeverageInspectionQuestionnaireResponseDto> => {
    const response = await apiClient.get<unknown>(`${beverageQuestionsBase}/phase/${phase}/current`);
    const parsed = parseCurrentBeverageInspectionQuestionnaireResponse(response.data);
    return {
      questionnaire: parsed.questionnaire,
      sections: normalizeQuestionnaireSections(parsed.sections ?? []),
      questions: normalizeBeverageQuestionTree(parsed.questions ?? []),
    };
  },

  getSubQuestionsForOption: async (questionId: string, selectedOptionId: string): Promise<BeverageQuestionTreeDto[]> => {
    const response = await apiClient.get<{ success: boolean; data: BeverageQuestionTreeDto[] }>(
      `/api/v1/beverage-questions/${questionId}/sub-questions?selectedOptionId=${encodeURIComponent(selectedOptionId)}`
    );
    return normalizeBeverageQuestionTree(response.data.data ?? []);
  },

  /** Records a PRE_EVENT/IN_EVENT/POST_EVENT inspection — the `phase` field on the dto picks the branch. */
  create: async (applicationId: string, dto: CreateBeverageInspectionDto): Promise<BeverageInspectionDto> => {
    const response = await apiClient.post<{ success: boolean; data: BeverageInspectionDto }>(
      `/api/v1/beverage-authorizations/${applicationId}/inspections`,
      dto
    );
    return response.data.data;
  },
};

export const beverageDashboardApi = {
  get: async (params?: {
    locationId?: string;
    from?: string;
    to?: string;
    beverageTypeId?: string;
    purpose?: BeverageEventPurpose;
    status?: BeverageAuthorizationStatus;
    groupBy?: 'beverageType' | 'purpose';
  }): Promise<BeverageDashboardDto> => {
    const response = await apiClient.get<{ success: boolean; data: BeverageDashboardDto }>(
      '/api/v1/beverage-dashboard',
      { params }
    );
    return response.data.data;
  },
};

export const beverageTypesApi = {
  list: async (includeInactive = false): Promise<BeverageTypeDto[]> => {
    const response = await apiClient.get<{ success: boolean; data: BeverageTypeDto[] }>('/api/v1/beverage-types', {
      params: includeInactive ? { includeInactive: 'true' } : undefined,
    });
    return response.data.data ?? [];
  },

  create: async (dto: { nameEn: string; nameRw: string; sortOrder?: number }): Promise<BeverageTypeDto> => {
    const response = await apiClient.post<{ success: boolean; data: BeverageTypeDto }>('/api/v1/beverage-types', dto);
    return response.data.data;
  },

  update: async (
    id: string,
    dto: { nameEn?: string; nameRw?: string; sortOrder?: number; isActive?: boolean }
  ): Promise<BeverageTypeDto> => {
    const response = await apiClient.put<{ success: boolean; data: BeverageTypeDto }>(
      `/api/v1/beverage-types/${id}`,
      dto
    );
    return response.data.data;
  },

  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/beverage-types/${id}`);
  },
};

export const beverageMonitoringApi = {
  get: async (params: { locationId?: string; period: string }): Promise<BeverageMonitoringDto> => {
    const response = await apiClient.get<{ success: boolean; data: BeverageMonitoringDto }>(
      '/api/v1/beverage-monitoring',
      { params }
    );
    return response.data.data;
  },

};

export const smsTemplatesApi = {
  list: async (): Promise<SmsTemplateDto[]> => {
    const response = await apiClient.get<{ success: boolean; data: SmsTemplateDto[] }>('/api/v1/sms/templates');
    return response.data.data ?? [];
  },

  update: async (
    code: string,
    dto: { contentEn: string; contentRw: string; description?: string; isActive?: boolean }
  ): Promise<SmsTemplateDto> => {
    const response = await apiClient.put<{ success: boolean; data: SmsTemplateDto }>(
      `/api/v1/sms/templates/${code}`,
      dto
    );
    return response.data.data;
  },

  messages: async (applicationId?: string) => {
    const response = await apiClient.get('/api/v1/sms/messages', {
      params: applicationId ? { applicationId } : undefined,
    });
    return response.data.data ?? [];
  },
};
